import { fetcher } from "@certn/api";
import { Link, showToast, Stack, Text } from "@certn/cascadia-react";
import type { CascadiaControlProps } from "@certn/forms";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { isQuickscreenBasePath } from "../../utils";

function useConsentFormDownloadEndpoint(
  generateDownloadCaseEndpointPath: string
) {
  const { caseId: contextCaseId } = useApplicationFormContext();
  const { caseId: paramCaseId } = useParams();
  const caseId = contextCaseId || paramCaseId;
  const { basePath } = useApplication();

  const apiBase = isQuickscreenBasePath(basePath)
    ? "/api/internal/client-portal/quickscreen-case"
    : "/api/internal/applicant/case";

  const url = caseId
    ? `${apiBase}/${caseId}/${generateDownloadCaseEndpointPath}`
    : null;

  return { url, caseId };
}

function ConsentFormDownload({
  label,
  linkLabel,
  generateDownloadCaseEndpointPath,
}: {
  label?: string;
  linkLabel?: string;
  generateDownloadCaseEndpointPath: string;
}) {
  const { t } = useTranslation();
  const { url, caseId } = useConsentFormDownloadEndpoint(
    generateDownloadCaseEndpointPath
  );
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!url || isDownloading) return;

    // Open the tab synchronously in the click handler so it isn't blocked as a
    // popup, then point it at the presigned URL once we have it.
    const newWindow = window.open("", "_blank");

    setIsDownloading(true);
    try {
      const data = await fetcher.request<{ presigned_url: string } | undefined>(
        url
      );
      if (data?.presigned_url) {
        if (newWindow) {
          newWindow.opener = null;
          newWindow.location.href = data.presigned_url;
        } else {
          window.open(data.presigned_url, "_blank", "noopener,noreferrer");
        }
      } else {
        newWindow?.close();
        showToast(
          t(
            "The consent document is being generated. Please try again shortly."
          ),
          { type: "info" }
        );
      }
    } catch {
      newWindow?.close();
      showToast(t("Failed to download document"), { type: "error" });
    } finally {
      setIsDownloading(false);
    }
  }, [url, isDownloading, t]);

  return (
    <Stack gap="space-1">
      {label && (
        <Text style="form/section-heading" testID="consent-form-download-label">
          {label}
        </Text>
      )}
      <Link
        testID="certn-contract-download-link"
        onPress={handleDownload}
        isDisabled={isDownloading || !caseId}
        textColor="text-info"
        textStyle="global/body-primary"
      >
        {isDownloading ? (
          <Trans>Downloading...</Trans>
        ) : (
          linkLabel || <Trans>Download consent form</Trans>
        )}
      </Link>
    </Stack>
  );
}

export function ConsentFormDownloadControl(props: CascadiaControlProps) {
  const { uischema, label } = props;
  const options = uischema.options || {};
  const generateDownloadCaseEndpointPath =
    options.generateDownloadCaseEndpointPath;
  const linkLabel = options.linkLabel;

  return (
    <ConsentFormDownload
      label={label}
      linkLabel={linkLabel}
      generateDownloadCaseEndpointPath={generateDownloadCaseEndpointPath}
    />
  );
}
